/**
 * Vitrine de horarios do perfil.
 *
 * Mostra os proximos horarios livres do dia corrente. Quando o dia ja acabou, a
 * experiencia publica ja aponta `date` para o proximo dia com vaga, entao aqui
 * basta nomear o dia que esta sendo mostrado.
 *
 * Como cada chip leva ao wizard na primeira etapa, sem carregar a escolha
 * (DEC-2), o rotulo acessivel informa dia e hora e diz que o toque comeca o
 * agendamento — sem prometer que aquele horario fica reservado.
 */

import Link from "next/link";
import { FiClock } from "react-icons/fi";
import { PreviewCarousel } from "./PreviewCarousel";
import {
  formatPublicDateLabel,
  isTodayYMD,
  resolveUpcomingTimes,
} from "../utils/publicDates";

interface ProfileTimesPreviewProps {
  date: string;
  times: string[];
  loading: boolean;
  bookingHref: string;
  /** Injetavel para o teste fixar o relogio. */
  now?: Date;
}

export function ProfileTimesPreview({
  date,
  times,
  loading,
  bookingHref,
  now,
}: ProfileTimesPreviewProps) {
  const reference = now || new Date();
  const upcoming = resolveUpcomingTimes(date, times, { now: reference });
  const isToday = isTodayYMD(date, reference);
  const dayLabel = formatPublicDateLabel(date);

  if (loading) {
    return (
      <section
        aria-label="Horários disponíveis"
        data-testid="profile-times-preview"
        className="rounded-2xl bg-white px-4 py-4 shadow-sm"
      >
        <h2 className="text-base font-bold text-[#233055]">Próximos horários</h2>
        <p className="mt-1 text-sm text-slate-600" role="status">
          Procurando horários livres...
        </p>
        <div
          aria-hidden="true"
          className="mt-3 flex gap-2 overflow-hidden"
          data-testid="profile-times-skeleton"
        >
          {[0, 1, 2, 3].map((index) => (
            <span
              key={index}
              className="h-11 w-20 shrink-0 animate-pulse rounded-xl bg-slate-100"
            />
          ))}
        </div>
      </section>
    );
  }

  if (upcoming.length === 0) return null;

  return (
    <section
      aria-label="Horários disponíveis"
      data-testid="profile-times-preview"
      className="rounded-2xl bg-white px-3 py-4 shadow-sm"
    >
      <div className="mb-1 px-1">
        <h2 className="text-base font-bold text-[#233055]">
          {isToday ? "Próximos horários de hoje" : "Próximos horários"}
        </h2>
        <p className="mt-0.5 text-sm text-slate-600">
          {isToday
            ? "Toque em um horário para começar seu agendamento."
            : // O rótulo termina na abreviação do mês ("out."): sem tirar o
              // ponto dela, a frase saía com dois pontos no fim.
              `Hoje não há mais vaga. O próximo dia livre é ${dayLabel.replace(/\.$/, "")}.`}
        </p>
      </div>

      <PreviewCarousel
        items={upcoming}
        getKey={(time) => time}
        renderItem={(time) => (
          <Link
            href={bookingHref}
            data-testid={`profile-time-card-${time}`}
            aria-label={`${
              isToday ? "Hoje" : dayLabel
            } às ${time}. Toque para começar o agendamento.`}
            className="flex min-h-[56px] items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white text-base font-bold text-[#233055] transition hover:border-primary-300 hover:text-primary-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
          >
            <FiClock className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <span aria-hidden="true">{time}</span>
          </Link>
        )}
        ariaLabel={isToday ? "Horários livres de hoje" : `Horários livres de ${dayLabel}`}
        previousLabel="Ver horário anterior"
        nextLabel="Ver próximo horário"
        itemWidthClass="w-[30%] sm:w-[16%]"
        emphasizeActive={false}
        testId="profile-times-carousel"
      />
    </section>
  );
}
